<template>
  <div class="fts-card-view">
    <div class="card-view-item" v-for="item in list" :key="item.id">
      <!-- Header -->
      <div class="card-view-header lv-nav lv-nav-dual-band">
        <div class="lv-nav-left">
          <div class="lv-nav-info">
            <div class="lv-nav-title-wrapper">
              <div class="lv-nav-title">{{ item.title }}</div>
              <div style="margin-left: 10px" v-if="item.extension.username">
                <fts-profile
                  class="audit-profile"
                  :userName="item.extension.username"
                />
              </div>
            </div>
            <div class="lv-nav-info-wrapper">
              <!-- document -->
              <div class="lv-nav-document">
                <a
                  :href="locusUrl(item.id)"
                  class="card-view-document-id lv-nav-document-id"
                  @click.prevent="executeActivity({ id: 'quickView' }, [item])"
                  v-if="viewRight"
                >
                  {{ item.documentId }}
                </a>
                <div class="card-view-document-id lv-nav-document-id" v-else>
                  {{ item.documentId }}
                </div>
              </div>
              <!-- audit updated -->
              <div class="fts-audit">
                <fts-profile
                  class="audit-profile"
                  :userName="item.audit.updatedBy"
                />
                <fts-tippy
                  class="audit-date-wrapper"
                  :content="moment(item.audit.updatedDate).format('llll')"
                >
                  <div slot="trigger" class="audit-date">
                    {{ moment(item.audit.updatedDate).fromNow() }}
                  </div>
                </fts-tippy>
              </div>
              <!-- assign To -->
              <fts-assign-to
                :formData="item"
                :view-mode="!assignRight"
                :comment-section="commentRight"
                class="card-view-assign"
                @refresh="refresh"
                v-if="assignColumn"
              />
            </div>
          </div>
        </div>
        <div class="lv-nav-right">
          <div class="lv-nav-actions">
            <!-- state -->
            <div class="lv-nav-state">
              <fts-state
                :state="item.state"
                v-if="item.state && item.state.id"
              />
            </div>
            <!-- collaboration -->
            <div
              class="lv-nav-action lv-nav-collaboration"
              v-if="commentRight && item.lastComment && item.lastComment.id"
            >
              <fts-tippy
                :options="{ theme: 'light-border' }"
                :request="{
                  dispatchName: 'loadHistory',
                  payload: {
                    id: item.lastComment.id,
                    type: item.lastComment.type
                      ? item.lastComment.type
                      : 'comment',
                  },
                }"
              >
                <div
                  slot="trigger"
                  class="collaboration-trigger collaboration-comment"
                  :class="{
                    'collaboration-last': (item.collaborationFlags & 4) === 4,
                  }"
                >
                  <i class="fts-icon-comment" />
                </div>
                <div
                  slot="content"
                  slot-scope="collaboration"
                  class="fts-collaboration-content"
                >
                  <div class="collaboration-comment-audit">
                    <fts-profile :userName="collaboration.createdBy" />
                    <div class="comment-from-now">
                      {{ moment(collaboration.createdDate).fromNow() }}
                    </div>
                  </div>
                  <div
                    class="collaboration-comment-content"
                    v-html="collaboration.detail"
                  ></div>
                  <div class="collaboration-date">
                    {{ moment(collaboration.createdDate).format("llll") }}
                  </div>
                </div>
              </fts-tippy>
              <span class="collaboration-count">{{ item.commentCount }}</span>
            </div>
            <!-- standard menu -->
            <fts-standard-menu       
              :item="item"
              :standardActions="standardActions"
              :moduleId="vectorId"
              :listId="listId"
              class="lv-nav-action"
              @executed="executeActivity($event, [item])"
            >
              <font-awesome-icon
                slot="toggle"
                :icon="['fas', 'ellipsis-v']"
                fixed-width
              />
            </fts-standard-menu>
          </div>
        </div>
      </div>
      <!-- Body -->
      <div class="card-view-body">
        <div
          v-for="column in getColumns"
          :key="column.id"
          class="card-view-column"
          :class="{
            'full-width':
              column.type === 11 || column.type === 13 || column.type === 14,
          }"
        >
          <div class="form-element-label">
            <fts-moji
              :emoji="moduleEmoji(column.connectionId)"
              :size="14"
              class="form-element-emoji"
              v-if="column.connectionId && moduleEmoji(column.connectionId)"
            />
            <i
              class="form-element-icon"
              :class="informationIcon(column.type)"
              v-else
            />
            <div class="form-element-name">{{ column.displayName }}</div>
          </div>
          <fts-list-view-display
            :formData="item.extension"
            :item="column"
            :vectorId="vectorId"
            comfortMode
          />
        </div>
      </div>
      <!-- Footer -->
      <div
        class="card-view-footer"
        v-if="
          getActivityList(item).length ||
          getActionList(item).length ||
          getDocumentList(item).length
        "
      >
        <div class="lv-actions">
          <!-- activities -->
          <fts-btn-hold
            outline
            :color="x.color || 'primary'"
            :emoji="x.emoji"
            text-transform-init
            class="btn-activity"
            @clickEvent="executeActivity(x, [item])"
            @longPressEvent="executeActivity(x, [item], true)"
            v-for="x in getActivityList(item)"
            :key="x.id"
          >
            <span class="btn-activity-label">{{ x.name }}</span>
          </fts-btn-hold>
          <!-- actions -->
          <fts-btn
            outline
            :color="x.color || 'ternary-blue'"
            :emoji="x.emoji"
            text-transform-init
            class="btn-activity"
            @click="executeAction(x, [item])"
            v-for="x in getActionList(item)"
            :key="x.id"
          >
            <span :class="{ 'btn-activity-label': !x.color }">{{
              x.name
            }}</span>
          </fts-btn>
          <!-- document -->
          <fts-btn
            outline
            color="ternary-green"
            text-transform-init
            class="btn-activity"
            @click="executeDocument(x, [item])"
            v-for="x in getDocumentList()"
            :key="x.id"
          >
            <font-awesome-icon :icon="['far', 'file-pdf']" class="btn-activity-icon fts-file-pdf" v-if="x.typeId === 'pdf'" />
            <font-awesome-icon :icon="['far', 'file-alt']" class="btn-activity-icon fts-file-excel" v-else-if="x.typeId === 'spreadsheet'" />
            <font-awesome-icon :icon="['far', 'file-code']" class="btn-activity-icon fts-file-html" v-else-if="x.typeId === 'html'" />
            <font-awesome-icon :icon="['far', 'file']" class="btn-activity-icon fts-file-default" v-else />
            <span class="btn-activity-label">{{x.name}}</span>
          </fts-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import commons from "../../commons/commons";
import { store as canvasDesignStore } from "../../store/canvasDesignStore";

import FTSStandardMenu from "../FTSStandardMenu.vue";
import FTSTippy from "../FTSTippy";
import FTSProfile from "../FTSProfile";
import FTSState from "../FTSState";
import FTSMoji from "../FTSMoji";
import FTSBtn from "../FTSBtn";
import FTSBtnHold from "../FTSBtnHold";
import FTSListViewDisplay from "../FTSListViewDisplay";

import FTSAssignTo from "../FTSAssignTo";

export default {
  props: {
    vectorId: [String, Number],
    listId: String,
    columns: {
      type: Array,
      default: () => [],
    },
    list: {
      type: Array,
      default: () => [],
    },
    standardActions: {
      type: Array,
      default: () => [],
    },
    actionList: {
      type: Array,
      default: () => [],
    },
    documentList: {
      type: Array,
      default: () => [],
    },
    activityList: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    "fts-standard-menu": FTSStandardMenu,
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-state": FTSState,
    "fts-moji": FTSMoji,
    "fts-btn": FTSBtn,
    "fts-btn-hold": FTSBtnHold,
    "fts-assign-to": FTSAssignTo,
    "fts-list-view-display": FTSListViewDisplay,
  },
  methods: {
    moment,
    informationIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    moduleEmoji(connectionId) {
      try {
        return this.modules.find((x) => x.id == connectionId).emoji;
      } catch (err) {}
    },
    locusUrl(id) {
      return commons.clientPathUrl({
        moduleId: this.vectorId,
        listId: this.listId,
        id: id,
      });
    },
    getActivityList(item) {
      return this.activityList.filter((activity) => {
        return (
          !activity.fromStates ||
          activity.fromStates.some((x) => x === item.state.id)
        );
      });
    },
    getActionList(item) {
      /* custom visibility */
      return this.actionList.filter((action) => {
        const items = [item]; 
        if (action.isDefault) {
          return false;
        } else if (Boolean(action.condition)) {
          try {
            return Boolean(eval(action.condition));
          } catch(err) {
            console.error('[Custom:Condition error]', err);
            return false;
          }
        }
        return true;
      });
    },
    getDocumentList() {
      return this.documentList.filter((action) => {
        if (
          action.dataSourceId == "collection" ||
          action.dataSourceId == "logicService"
        )
          return false;
        return true;
      });
    },
    executeActivity(activity, items, fullView) {
      this.$emit("activityExecuted", {
        activity: activity,
        items: items,
        fullView: fullView,
      });
    },
    executeAction(action, items) {
      this.$emit("actionExecuted", {
        action: action,
        items: items,
      });
    },
    executeDocument(action, items) {
      this.$emit("documentExecuted", {
        action: action,
        items: items,
      });
    },
    refresh() {
      this.$emit("refresh");
    },
  },
  computed: {
    // rights
    viewRight() {
      return (
        this.standardActions.indexOf("view") > -1 ||
        this.standardActions.indexOf("quickView") > -1
      );
    },
    assignRight() {
      return this.standardActions.indexOf("assign") > -1;
    },
    commentRight() {
      return this.standardActions.indexOf("comment") > -1;
    },
    getColumns() {
      return this.columns.filter((item) => {
        return (
          item.fieldName &&
          item.fieldName != "documentId" &&
          item.fieldName != "state.name" &&
          !item.system
        );
      });
    },
    modules() {
      try {
        return canvasDesignStore.state.workspace.menus;
      } catch (err) {}

      return [];
    },
    assignColumn() {
      return this.columns.find((x) => x.system && x.fieldName === "assignTo");
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";
@import "/scss/style.scss";

.fts-card-view {
  margin-left: 1rem;
  margin-right: 1rem;
  color: var(--fts-text);
  .card-view-item {
    max-width: 1350px;
    margin: auto;
    box-shadow: var(--fts-elevation-3);
    border-radius: 0.25rem;
    background: var(--fts-card-background);
    &:not(:last-child) {
      margin-bottom: 1rem;
    }
    > div {
      &:first-child {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
      }
      &:last-child {
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
      }
    }
    .card-view-header {
      padding: 0.75rem 0;
      &:hover {
        a.card-view-document-id {
          color: $primaryColor;
          cursor: pointer;
        }
      }
      .card-view-document-id {
        transition: color 0.3s ease-in-out;
        text-decoration: none;
        color: var(--fts-text);
      }
      .card-view-assign {
        margin-left: 0.75rem;
      }
    }
    .card-view-body {
      min-height: 100px;
      display: flex;
      flex-wrap: wrap;
      .card-view-column {
        padding: 1rem;
        flex: 1 1 33.333333%;
        &.full-width {
          max-width: 100%;
        }
        &:not(.full-width) {
          max-width: 33.333333%;
        }
        .form-element-label {
          display: flex;
          align-items: center;
          margin-bottom: 0.175rem;
          color: var(--fts-gray-5);
          font-size: 0.75rem;
          .form-element-emoji {
            margin-right: 0.575rem;
          }
          .form-element-icon {
            margin-right: 0.575rem;
            color: inherit;
            font-size: inherit;
          }
          .form-element-name {
            @include text-overflow-ellipsis;
            color: inherit;
            font-size: inherit;
          }
        }
        // Override
        .fts-display-table.comfort-design > tr > th {
          .col-header {
            font-size: 0.75rem;
            .col-header-icon {
              margin-right: 0.575rem;
              color: inherit;
              font-size: inherit;
            }
          }
        }
        // End Override
      }
    }
    .card-view-footer {
      padding: 1rem;
      border-top: 1px solid var(--fts-gray-3);
    }
  }
}
</style>